// Single source of truth for the homepage's <title>, meta description, and JSON-LD.
// Consumed by BOTH src/pages/Index.tsx (client-side Helmet, for JS-rendering crawlers
// and users) and scripts/prerenderRoutes.ts (build-time static HTML, for non-JS
// crawlers — GPTBot, ClaudeBot, PerplexityBot, Google-Extended all fetch raw HTML and
// do not execute JavaScript). Keeping one source prevents the two from drifting, which
// previously caused the client to overwrite a correct build-time title with a stale one
// on hydration.
//
// Shipped as a single "@graph" object (not an array of top-level schemas) so exactly one
// <script type="application/ld+json"> renders per page — avoids duplicate/competing
// Organization or WebPage entries between this and any other schema source.

import { homepageFaqs } from "@/data/homepageFaqData";

const BASE_URL = "https://www.homeapproach.com";

export const HOMEPAGE_TITLE = "Home Approach Stack — Every Down Payment Dollar, One Total";
export const HOMEPAGE_DESCRIPTION =
  "The Home Approach Stack combines federal, state, county, and employer aid into one free total — plus Homie, your AI homebuying guide, and free tools for every step.";

export const HOMEPAGE_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      name: "Home Approach",
      url: `${BASE_URL}/`,
      description: HOMEPAGE_DESCRIPTION,
      // No SearchAction: there is no live user-facing site search backing a
      // "?q={search_term_string}" query today. Do not add one back without a real
      // search implementation to point it at.
    },
    {
      "@type": ["Organization", "FinancialService"],
      name: "Home Approach",
      alternateName: "HomeApproach",
      url: `${BASE_URL}/`,
      logo: {
        "@type": "ImageObject",
        url: `${BASE_URL}/og-image.png`,
        width: 1200,
        height: 630,
      },
      description:
        "Home Approach provides free homeownership tools, mortgage calculators, grant-matching, AI guidance (Homie), and expert education to help first-time buyers, homeowners, sellers, and investors at every stage.",
      foundingDate: "2023",
      email: "info@homeapproach.com",
      areaServed: "US",
      serviceType: "Homeownership Education and Tools",
      knowsAbout: [
        "First-time homebuying",
        "Mortgage pre-approval",
        "FHA loans",
        "VA loans",
        "Down payment assistance",
        "Mortgage calculators",
        "Home equity",
        "Refinancing",
        "Real estate investing",
        "Home maintenance",
        "Credit score improvement",
        "Grant programs for homebuyers",
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Free Homeownership Tools",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mortgage Calculator", url: `${BASE_URL}/mortgage-calculators` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Home Approach Stack", url: `${BASE_URL}/stack` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Homie AI Guide", url: `${BASE_URL}/homie` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Affordability Simulator", url: `${BASE_URL}/affordability-simulator` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Down Payment Planner", url: `${BASE_URL}/down-payment-planner` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Neighborhood Explorer", url: `${BASE_URL}/neighborhood-explorer` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Rent vs Buy Calculator", url: `${BASE_URL}/rent-vs-buy` } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Refinance Optimizer", url: `${BASE_URL}/refi-optimizer` } },
        ],
      },
      sameAs: ["https://twitter.com/HomeApproach"],
    },
    {
      "@type": "WebPage",
      "@id": `${BASE_URL}/`,
      url: `${BASE_URL}/`,
      name: HOMEPAGE_TITLE,
      description: HOMEPAGE_DESCRIPTION,
      isPartOf: { "@type": "WebSite", url: `${BASE_URL}/` },
      about: [{ "@type": "Thing", name: "First-time home buying" }],
    },
    {
      "@type": "SoftwareApplication",
      name: "Homie",
      alternateName: "Homie by Home Approach",
      url: `${BASE_URL}/homie`,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      description:
        "Homie is a straight-talking AI homebuying guide from Home Approach. Homie knows your situation, tells you the truth, and stays with you from first credit check to closing day — and beyond.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Home Approach" },
    },
    {
      "@type": "SoftwareApplication",
      name: "Home Approach Stack",
      alternateName: "Stack",
      url: `${BASE_URL}/stack`,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      description:
        "Stack is the only 4-layer down-payment assistance engine. It searches federal, state, county, and employer programs and combines every dollar you qualify for into one total.",
      featureList: [
        "Federal down-payment programs (FHA, VA, USDA)",
        "Statewide DPA grants",
        "County and city assistance",
        "Employer housing benefits",
      ],
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Home Approach" },
    },
    {
      "@type": "FAQPage",
      // Sourced directly from src/components/home/HomepageFaq.tsx's own faqs
      // array so this schema can never drift from the visible on-page copy —
      // required by schema.org / Google structured-data policy.
      mainEntity: homepageFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};
